import React from 'react';
import styled from '@emotion/styled';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Block as RevokeIcon, DeleteForever as DeleteForeverIcon } from '@mui/icons-material';
import { groupScopes } from '../utils/apiScopeUtils';
import { statusSeverity } from '../utils/statusColors';
import { formatTimeAgo, formatDateTime, formatDayShort } from '../utils/dateUtils';

const LoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    textAlign: 'center',
}));

const ScopeChips = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    maxWidth: 320,
}));

const MonospaceCaption = styled(Typography)({
    fontFamily: 'monospace',
});

const ActionCell = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
}));

// The server computes the status so the row chip and the admin status filter can never disagree.
const keyState = (apiKey) => {
    const label = apiKey.status || (apiKey.revokedAt ? 'Revoked' : 'Active');
    return { label, color: statusSeverity('apiKey', label) };
};

const stamp = (value) => formatDateTime(value) || '—';

// Live keys first, newest first within each group.
const sortKeys = (keys) => [...keys].sort((a, b) => {
    if (!!a.revokedAt !== !!b.revokedAt) return a.revokedAt ? 1 : -1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
});

const ApiKeyList = ({ apiKeys = [], domains = [], loading = false, onRevoke, onDelete, showIp = false, showUser = false, emptyText = 'No API keys yet.' }) => {
    if (loading) {
        return (
            <LoadingBox>
                <CircularProgress />
            </LoadingBox>
        );
    }

    // A paged list is already ordered by the server; re-sorting would only shuffle within the page.
    const rows = showUser ? apiKeys : sortKeys(apiKeys);

    if (!apiKeys.length) {
        return (
            <EmptyState>
                <Typography variant="body2" color="text.secondary">
                    {emptyText}
                </Typography>
            </EmptyState>
        );
    }

    return (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {showUser && <TableCell><strong>Person</strong></TableCell>}
                        <TableCell><strong>Label</strong></TableCell>
                        <TableCell><strong>Access</strong></TableCell>
                        <TableCell><strong>Created</strong></TableCell>
                        <TableCell><strong>Last used</strong></TableCell>
                        {showIp && <TableCell><strong>Last IP</strong></TableCell>}
                        <TableCell><strong>Expires</strong></TableCell>
                        <TableCell align="center"><strong>Status</strong></TableCell>
                        <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((apiKey) => {
                        const state = keyState(apiKey);
                        const groups = groupScopes(apiKey.scopes, domains);
                        return (
                            <TableRow key={apiKey._id} hover>
                                {showUser && (
                                    <TableCell>
                                        <Typography variant="body2">{apiKey.user?.fullName || 'Unknown'}</Typography>
                                        <Typography variant="caption" color="text.secondary">{apiKey.user?.email || '—'}</Typography>
                                    </TableCell>
                                )}
                                <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                        {apiKey.label}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <ScopeChips>
                                        {groups.map((group) => (
                                            <Chip key={group.domain} size="small" variant="outlined" label={`${group.label}: ${group.actions}`} />
                                        ))}
                                    </ScopeChips>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">{stamp(apiKey.createdAt)}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">{apiKey.lastUsedAt ? formatTimeAgo(apiKey.lastUsedAt) : 'Never'}</Typography>
                                </TableCell>
                                {showIp && (
                                    <TableCell>
                                        <MonospaceCaption variant="caption">{apiKey.lastUsedIp || '—'}</MonospaceCaption>
                                    </TableCell>
                                )}
                                <TableCell>
                                    <Typography variant="caption">{apiKey.expiresAt ? formatDayShort(apiKey.expiresAt) : 'Never'}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Chip size="small" variant="outlined" color={state.color} label={state.label} />
                                </TableCell>
                                <TableCell align="right">
                                    <ActionCell>
                                        {apiKey.revokedAt ? (
                                            <Typography variant="caption" color="text.secondary">
                                                Revoked {stamp(apiKey.revokedAt)}
                                            </Typography>
                                        ) : (
                                            <Tooltip title="Revoke this key">
                                                <IconButton size="small" color="error" onClick={() => onRevoke && onRevoke(apiKey)}>
                                                    <RevokeIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {onDelete && (
                                            <Tooltip title="Delete this key permanently">
                                                <IconButton size="small" color="error" onClick={() => onDelete(apiKey)}>
                                                    <DeleteForeverIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </ActionCell>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ApiKeyList;
